import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import * as XLSX from "xlsx";
import Table from "../../components/Tables/Table";
import {
  Divider,
  Drawer,
  Form,
  Modal,
  Select,
  Space,
  Spin,
  Tooltip,
} from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning, IoMdCloudDownload } from "react-icons/io";
import { useNavigate } from "react-router-dom";

import {
  createStaff,
  deleteStaff,
  exportStaffTemplate,
  getStaffs,
  importStaffTemplate,
  updateStaff,
} from "../../services/staffService";
import { staffSchema } from "../../validators/staffValidators";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import { getInstitutions } from "../../services/institutionService";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getPositions } from "../../services/positionService";
import InputPhone from "../../components/Inputs/InputPhone";
import { BiShow } from "react-icons/bi";

import StaffTableHeader from "../../components/Tables/StaffTableHeader";
import { getTitles } from "../../services/titleService";
import DateInput from "../../components/Inputs/DateInput";

import {
  HiOutlineBuildingOffice2,
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlineBriefcase,
  HiOutlineCalendarDays,
  HiOutlineAcademicCap,
  HiOutlineBeaker,
} from "react-icons/hi2";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Staff = () => {
  const [staffs, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [staffToEdit, setStaffToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [titles, setTitles] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  const [bulkUpload, setBulkUpload] = useState([]);
  const [fileUpload, setFileUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  const { user } = useStateContext();

  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadStaff();
    loadInstitutions();
    loadPositions();
    loadTitles();
  }, []);

  useEffect(() => {
    let filtered = staffs;

    if (searchTerm.trim() !== "") {
      filtered = staffs.filter((item) =>
        `${item.first_name} ${item.last_name} ${item.institution.name} ${item.position.name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredStaff(filtered);
  }, [searchTerm, staffs, selectedInstitution]);

  const loadInstitutions = async () => {
    setLoading(true);
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      console.log(error);

      if (error) {
        toast.error(
          error?.response?.data?.error || "Failed to load Institutions"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPositions = async () => {
    setLoading(true);
    try {
      const { data } = await getPositions();
      setPositions(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load positions");
    } finally {
      setLoading(false);
    }
  };

  const loadTitles = async () => {
    setLoading(true);
    try {
      const { data } = await getTitles();
      setTitles(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load titles");
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    setLoading(true);
    try {
      const { data } = await getStaffs();
      setStaff(data.data);
      setFilteredStaff(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (staffToEdit) {
      onUpdateStaff(payload, resetForm);
    } else {
      onCreateStaff(payload, resetForm);
    }
  };

  const onCreateStaff = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createStaff(payload);

      setStaff((prev) => [...prev, data.data]);

      toast.success("Staff created successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response.data.error || "Staff was not created, try again!");
      }
    }
  };

  // ------------ HANDLE FILE UPLOAD FUNCTION ---------------------------------------

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    setFileUpload(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const expectedKeys = [
        "First name",
        "Last name",
        "Title",
        "Research interests",
        "Qualifications",
        "Contact",
        "Institution",
        "Position",
      ];

      const cleanedData = jsonData.map((row) => {
        const filtered = {};
        expectedKeys.forEach((key) => {
          filtered[key] = row[key] ?? "";
        });
        return filtered;
      });

      setBulkUpload(cleanedData);
    };
    reader.readAsArrayBuffer(file);
  };

  // ------------------------ HANDLE USER UPLOAD -----------------------------------

  const handleUserUpload = async () => {
    if (fileUpload) {
      const formData = new FormData();
      formData.append("file", fileUpload);
      setIsUploading(true);

      try {
        const { data } = await importStaffTemplate(formData);
        toast.success("Staff imported successfully!");
        loadStaff();
      } catch (error) {
        if (error?.response?.data?.errors) {
          const errors = error.response.data.errors;

          errors.forEach((err) => {
            if (Array.isArray(err)) {
              err.forEach((message) => {
                toast.error(message, { duration: 10000 });
              });
            } else {
              toast.error("An unexpected error occurred in the errors array.", {
                duration: 5000,
              });
            }
          });
        } else {
          toast.error(
            error?.response?.data?.error ||
              "An unexpected error occurred. Please try again.",
            { duration: 5000 }
          );
        }
      } finally {
        setIsUploading(false);
        setOpenDrawer(false);
      }
    }
  };

  // ---------------------- BULK UPLOAD COLUMNS ------------------------------------

  const bulkUploadColumns = [
    {
      title: "Title",
      dataIndex: "Title",
      key: "Title",
      render: (text) => (text ? text : ""),
    },
    {
      title: "First Name",
      dataIndex: "First name",
      key: "First name",
      render: (text) => (text ? text : ""),
    },
    {
      title: "Last Name",
      dataIndex: "Last name",
      key: "Last name",
      render: (text) => (text ? text : ""),
    },
    {
      title: "Institution",
      dataIndex: "Institution",
      key: "Institution",
      render: (text) => (text ? text : ""),
    },
  ];

  const handleEdit = (category) => {
    setStaffToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateStaff = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalStaff = [...staffs];

      const staffIndex = originalStaff.findIndex(
        (staff) => staff.id === staffToEdit.id
      );

      if (staffIndex === -1) throw new Error("Staff not found.");

      const { data } = await updateStaff(staffToEdit.id, payload);

      originalStaff[staffIndex] = data.data;

      setStaff(originalStaff);

      setStaffToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Staff updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response?.data?.error || "Staff not updated, try again!");
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.first_name?.toUpperCase()}`
    );
    setStaffToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteStaff = async () => {
    const originalStaff = [...staffs];

    const newStaff = originalStaff.filter(
      (staff) => staff.id !== staffToDelete.id
    );

    setStaff(newStaff);

    try {
      await deleteStaff(staffToDelete?.id);

      toast.success("Staff deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Staff not deleted, try again later"
        }`
      );

      setStaff(originalStaff);
    } finally {
      setIsDeleteModalOpen(false);

      setStaffToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setStaffToEdit(null);
    setStaffToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, record) =>
        `${record.title?.name}. ${record?.first_name}  ${record?.last_name}`,
    },

    {
      title: "Contact",
      dataIndex: "contact",
      key: "contact",

      render: (_, record) => {
        const isEmpty = !record.contact?.trim();
        const displayText = isEmpty ? "N/A" : record?.contact;

        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <>+{record?.contact}</>
        );
      },
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      render: (_, record) => `${record?.position?.name}`,
    },

    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),

    {
      title: "Starting Date",
      dataIndex: "date_started",
      key: "date_started",
    },

    {
      title: "End Date",
      dataIndex: "date_ended",
      key: "date_ended",
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Staff">
            <button
              className="p-2 border border-blue-200 rounded-md text-blue-600 hover:bg-blue-50 transition"
              onClick={() => {
                setSelectedStaff(record);
                setIsDrawerOpen(true);
              }}
            >
              <BiShow size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Edit Staff">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Staff">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Staff" subtitle1="Staff" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <StaffTableHeader
            title="Staff List"
            count={filteredStaff.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttons={[
              {
                label: "Bulk Import",
                onClick: () => setOpenDrawer(true),
              },
            ]}
            onButtonAddButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredStaff.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {staffToEdit ? "Edit Staff" : "Add Staff"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
            width={1000}
          >
            <Formik
              initialValues={{
                first_name: staffToEdit ? staffToEdit?.first_name : "",
                last_name: staffToEdit ? staffToEdit?.last_name : "",
                contact: staffToEdit ? staffToEdit?.contact : "",

                institution_id: staffToEdit
                  ? staffToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
                position_id: staffToEdit ? staffToEdit?.position.id : "",
                title_id: staffToEdit ? staffToEdit?.title?.id : "",
                research_interests: staffToEdit
                  ? staffToEdit?.research_interests
                  : "",
                qualifications: staffToEdit ? staffToEdit?.qualifications : "",
                date_started: staffToEdit ? staffToEdit?.date_started : "",
                date_ended: staffToEdit ? staffToEdit?.date_ended : "",
              }}
              enableReinitialize
              validationSchema={staffSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                submitForm,
                setFieldValue,
                touched,
                setFieldTouched,
              }) => {
                return (
                  <Form className="space-y-3 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <div className="grid gap-x-3 grid-cols-1 ">
                      {isInstAdmin ? (
                        <input
                          type="hidden"
                          name="institution_id"
                          value={
                            staffToEdit
                              ? staffToEdit?.institution?.id
                              : user?.institution?.id
                          }
                        />
                      ) : (
                        <CustomDropDownInput
                          name="institution_id"
                          title="Institution"
                          placeholder="Select an institution"
                          requiredField
                          errors={errors}
                          values={values}
                          touched={touched}
                          displayLabel="name"
                          idLabel="id"
                          options={institutions}
                          setFieldValue={setFieldValue}
                        />
                      )}
                    </div>

                    <div className="grid gap-x-3 grid-cols-1 md:grid-cols-2">
                      <CustomDropDownInput
                        title="Leadership Position"
                        multipleSelection={false}
                        name="position_id"
                        placeholder="Select Leadership Position"
                        requiredField={true}
                        errors={errors}
                        options={positions}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        values={values}
                        displayLabel="name"
                        setFieldValue={setFieldValue}
                        key="position_id"
                        showSearch={true}
                      />

                      <InputPhone
                        title="Phone Contact"
                        name="contact"
                        type="text"
                        placeholder="77XXXXXXX"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                    </div>

                    <div className="grid gap-x-3 grid-cols-1 md:grid-cols-6">
                      <CustomDropDownInput
                        title="Title"
                        multipleSelection={false}
                        name="title_id"
                        placeholder="Select Staff Title"
                        requiredField={true}
                        errors={errors}
                        options={titles}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        values={values}
                        displayLabel="name"
                        setFieldValue={setFieldValue}
                        key="title_id"
                        showSearch={true}
                      />

                      <div className="grid gap-x-2 col-span-5 grid-cols-1 md:grid-cols-2">
                        <TextInput
                          name="first_name"
                          title="First Name"
                          placeholder="E.g John"
                          requiredField={true}
                          errors={errors}
                          values={values}
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          autoFocus
                        />

                        <TextInput
                          name="last_name"
                          title="Last Name"
                          placeholder="E.g Doe"
                          requiredField={true}
                          errors={errors}
                          values={values}
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="grid gap-x-3 grid-cols-2">
                      <DateInput
                        name="date_started"
                        title="Starting Date"
                        placeholder="Select Starting date"
                        errors={errors}
                        values={values}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                      />

                      <DateInput
                        name="date_ended"
                        title="End Date"
                        placeholder="Select End date"
                        errors={errors}
                        values={values}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                      />
                    </div>

                    <div className="grid gap-x-3 grid-cols-1 md:grid-cols-2 ">
                      <RichTextEditor
                        name="research_interests"
                        title="Research Interests"
                        placeholder="E.g Science, Engineering, Astronomy..."
                        errors={errors}
                        values={values}
                        setFieldValue={setFieldValue}
                      />

                      <RichTextEditor
                        name="qualifications"
                        title="Qualifications"
                        placeholder="E.g Masters, Bachelors..."
                        errors={errors}
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                    </div>

                    <div className="grid gap-x-3 grid-cols-1 md:grid-cols-2 "></div>

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteStaff}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />

          {/* <Drawer
            title={
              <div className="text-lg font-semibold text-gray-700">
                {selectedStaff?.title.name} {selectedStaff?.first_name}{" "}
                {selectedStaff?.last_name}
              </div>
            }
            width={520}
            onClose={() => setIsDrawerOpen(false)}
            open={isDrawerOpen}
            placement="right"
          >
            {selectedStaff ? (
              <div className="text-sm text-gray-700 space-y-4">
                <div className="space-y-1">
                  <p>
                    <strong>Institution:</strong>{" "}
                    {selectedStaff.institution?.name || "—"}
                  </p>
                  <p>
                    <strong>Position:</strong>{" "}
                    {selectedStaff.position?.name || "—"}
                  </p>
                  <p>
                    <strong>Contact:</strong> {selectedStaff.contact || "—"}
                  </p>
                  <p>
                    <strong>Serving Period:</strong>{" "}
                    {selectedStaff.date_started
                      ? `${dayjs(selectedStaff.date_started).format(
                          "MMM YYYY"
                        )} - ${
                          selectedStaff.date_ended
                            ? dayjs(selectedStaff.date_ended).format("MMM YYYY")
                            : "Present"
                        }`
                      : "—"}
                  </p>
                </div>

                <Divider />

                <div>
                  <h4 className="text-sm font-semibold mb-1">Qualifications</h4>
                  <div
                    className="prose max-w-full text-sm text-gray-600"
                    dangerouslySetInnerHTML={{
                      __html: selectedStaff.qualifications || "<p>—</p>",
                    }}
                  />
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-1">
                    Research Interests
                  </h4>
                  <div
                    className="prose max-w-full text-sm text-gray-600"
                    dangerouslySetInnerHTML={{
                      __html: selectedStaff.research_interests || "<p>—</p>",
                    }}
                  />
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No staff selected.</p>
            )}
          </Drawer> */}

          <Drawer
            title={
              <div className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <HiOutlineUser className="text-blue-600" />
                {selectedStaff?.title?.name} {selectedStaff?.first_name}{" "}
                {selectedStaff?.last_name}
              </div>
            }
            width={520}
            onClose={() => setIsDrawerOpen(false)}
            open={isDrawerOpen}
            placement="right"
          >
            {selectedStaff ? (
              <div className="text-sm text-gray-700 space-y-6">
                {/* General Information */}
                <div>
                  <h3 className="font-semibold text-blue-700 border-b pb-1 mb-3 flex items-center gap-2">
                    <HiOutlineBriefcase className="text-blue-500" /> General
                    Information
                  </h3>
                  <div className="space-y-2">
                    <p className="flex items-center gap-2">
                      <HiOutlineBuildingOffice2 className="text-gray-500" />
                      <span className="font-medium text-gray-600">
                        Institution:
                      </span>{" "}
                      <span>{selectedStaff.institution?.name || "—"}</span>
                    </p>

                    <p className="flex items-center gap-2">
                      <HiOutlineBriefcase className="text-gray-500" />
                      <span className="font-medium text-gray-600">
                        Position:
                      </span>{" "}
                      <span>{selectedStaff.position?.name || "—"}</span>
                    </p>

                    <p className="flex items-center gap-2">
                      <HiOutlinePhone className="text-gray-500" />
                      <span className="font-medium text-gray-600">
                        Contact:
                      </span>{" "}
                      <span>{selectedStaff.contact || "—"}</span>
                    </p>

                    <p className="flex items-center gap-2">
                      <HiOutlineCalendarDays className="text-gray-500" />
                      <span className="font-medium text-gray-600">
                        Serving Period:
                      </span>{" "}
                      <span>
                        {selectedStaff.date_started
                          ? `${selectedStaff.date_started} - ${
                              selectedStaff.date_ended || "Present"
                            }`
                          : "—"}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Qualifications */}
                <div>
                  <h3 className="font-semibold text-green-700 border-b pb-1 mb-2 flex items-center gap-2">
                    <HiOutlineAcademicCap className="text-green-500" />{" "}
                    Qualifications
                  </h3>
                  <div
                    className="prose prose-sm max-w-full text-gray-700"
                    dangerouslySetInnerHTML={{
                      __html: selectedStaff.qualifications || "<p>—</p>",
                    }}
                  />
                </div>

                {/* Research Interests */}
                <div>
                  <h3 className="font-semibold text-purple-700 border-b pb-1 mb-2 flex items-center gap-2">
                    <HiOutlineBeaker className="text-purple-500" /> Research
                    Interests
                  </h3>
                  <div
                    className="prose prose-sm max-w-full text-gray-700"
                    dangerouslySetInnerHTML={{
                      __html: selectedStaff.research_interests || "<p>—</p>",
                    }}
                  />
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No staff selected.</p>
            )}
          </Drawer>

          {/* -----------------BULK IMPORT STAFF ------------------------ */}

          <Drawer
            title="Bulk Import Staff"
            width={720}
            onClose={() => setOpenDrawer(false)}
            open={openDrawer}
            extra={
              <Space>
                {bulkUpload?.length > 0 && (
                  <FormButton
                    text="Upload"
                    type="primary"
                    onClick={handleUserUpload}
                    tooltip="Upload the selected staff records"
                  />
                )}
                <FormButton
                  onClick={() => setOpenDrawer(false)}
                  text="Cancel"
                  tooltip="Close without uploading"
                />
              </Space>
            }
          >
            {/* Loading indicator */}
            {isUploading && (
              <div className="text-center my-6">
                <Spin size="large" />
                <p className="mt-2 text-gray-600">Uploading staff...</p>
              </div>
            )}

            {/* Export template section */}
            <div className="mb-6">
              <h3 className="text-md font-semibold mb-2">
                Step 1: Download Template
              </h3>
              <FormButton
                text="Export Template"
                icon={<IoMdCloudDownload />}
                type="default"
                className="border border-blue-500 text-blue-600  hover:bg-blue-50"
                onClick={async () => {
                  try {
                    const response = await exportStaffTemplate();
                    const blob = new Blob([response.data], {
                      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = "Staff registration template.xlsx";
                    link.click();
                  } catch {
                    toast.error("Failed to export template");
                  }
                }}
              />
            </div>

            {/* File upload section */}
            <div className="mb-6">
              <h3 className="text-md font-semibold mb-2">
                Step 2: Upload Filled Template
              </h3>
              <label
                htmlFor="file-upload"
                className="inline-block px-5 py-2 bg-green-600 text-white rounded-md cursor-pointer hover:bg-green-700 transition"
              >
                Select File to Upload
              </label>
              <input
                id="file-upload"
                type="file"
                onChange={handleFileUpload}
                className="hidden"
              />
              {fileUpload ? (
                <p className="text-sm text-gray-700 mt-2">
                  Selected file:{" "}
                  <span className="font-medium">{fileUpload.name}</span>
                </p>
              ) : (
                <p className="text-sm text-gray-400 mt-2">
                  No file selected yet.
                </p>
              )}
            </div>

            {/* Preview table */}
            {bulkUpload?.length > 0 ? (
              <div className="mt-6">
                <h2 className="font-bold text-lg mb-3">
                  Bulk Upload Preview ({bulkUpload.length} staff found)
                </h2>
                <Table
                  rows={bulkUpload}
                  columns={bulkUploadColumns}
                  pagination={{ pageSize: 10 }}
                  pageSize={10}
                  rowKey={(record) =>
                    `${record.Title}-${record["First name"]}-${record["Last name"]}`
                  }
                />
              </div>
            ) : (
              fileUpload && (
                <div className="mt-4 text-sm text-yellow-600">
                  No valid staff data found in the uploaded file.
                </div>
              )
            )}
          </Drawer>
        </div>
      </div>
    </>
  );
};

export default Staff;
